/**
 * Approval Rules Service
 * 
 * Manages configurable approval workflows for posts based on roles, content types,
 * keywords, or blanket requirements.
 */

import { supabase } from "@/integrations/supabase/client";
import { withServiceErrorHandling } from "@/lib/service-error";
import { logger } from "@/lib/structured-logger";

import { Json } from '@/integrations/supabase/types';

export type RuleType = 'role-based' | 'content-type' | 'keyword-trigger' | 'all-posts';

export interface ApprovalRule {
  id: string;
  organization_id: string;
  name: string;
  rule_type: RuleType;
  rule_config: Json;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface RoleBasedConfig {
  requester_role: string;
  approver_role: string;
}

export interface ContentTypeConfig {
  content_types: string[]; // e.g., ['promotional', 'product-launch']
}

export interface KeywordTriggerConfig {
  keywords: string[];
}

export class ApprovalRulesService {
  private static readonly logger = logger.child({ service: 'ApprovalRulesService' });

  /**
   * Get all approval rules for organization
   */
  static async getApprovalRules(organizationId: string): Promise<ApprovalRule[]> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching approval rules', { organizationId });

      const { data, error } = await supabase
        .from('approval_rules')
        .select('*')
        .eq('organization_id', organizationId)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      this.logger.info('Approval rules fetched', { 
        organizationId, 
        count: data?.length || 0 
      });

      return (data || []) as ApprovalRule[];
    }, { operation: 'getApprovalRules', table: 'approval_rules' });
  }

  /**
   * Create new approval rule
   */
  static async createApprovalRule(
    organizationId: string,
    rule: Omit<ApprovalRule, 'id' | 'organization_id' | 'created_at' | 'updated_at'>
  ): Promise<ApprovalRule> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Creating approval rule', { organizationId, rule });

      const { data, error } = await supabase
        .from('approval_rules')
        .insert({
          organization_id: organizationId,
          ...rule,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No data returned from insert');

      this.logger.info('Approval rule created', { ruleId: data.id });

      return data as ApprovalRule;
    }, { operation: 'createApprovalRule', table: 'approval_rules' });
  }

  /**
   * Update existing approval rule
   */
  static async updateApprovalRule(
    ruleId: string,
    updates: Partial<Omit<ApprovalRule, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>
  ): Promise<ApprovalRule> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating approval rule', { ruleId, updates });

      const { data, error } = await supabase
        .from('approval_rules')
        .update(updates)
        .eq('id', ruleId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Approval rule not found');

      this.logger.info('Approval rule updated', { ruleId });

      return data as ApprovalRule;
    }, { operation: 'updateApprovalRule', table: 'approval_rules' });
  }

  /**
   * Delete approval rule
   */
  static async deleteApprovalRule(ruleId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Deleting approval rule', { ruleId });

      const { error } = await supabase
        .from('approval_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;

      this.logger.info('Approval rule deleted', { ruleId });
    }, { operation: 'deleteApprovalRule', table: 'approval_rules' });
  }

  /**
   * Evaluate if a post requires approval based on active rules
   */
  static async evaluateApprovalRequirement(
    postId: string,
    organizationId: string,
    userRole: string,
    postContent: string
  ): Promise<{ requiresApproval: boolean; matchedRules: ApprovalRule[] }> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Evaluating approval requirement', { postId, organizationId, userRole });

      const rules = await this.getApprovalRules(organizationId);
      const activeRules = rules.filter(r => r.is_active);

      const matchedRules: ApprovalRule[] = [];

      for (const rule of activeRules) {
        let matches = false;

        switch (rule.rule_type) {
          case 'all-posts':
            matches = true;
            break;

          case 'role-based':
            const roleConfig = rule.rule_config as unknown as RoleBasedConfig;
            matches = userRole === roleConfig.requester_role;
            break;

          case 'keyword-trigger':
            const keywordConfig = rule.rule_config as unknown as KeywordTriggerConfig;
            matches = keywordConfig.keywords.some(keyword =>
              postContent.toLowerCase().includes(keyword.toLowerCase())
            );
            break;

          case 'content-type':
            // Would need post metadata to determine content type
            // For now, skip this rule type
            break;
        }

        if (matches) {
          matchedRules.push(rule);
        }
      }

      const requiresApproval = matchedRules.length > 0;

      this.logger.info('Approval evaluation complete', {
        postId,
        requiresApproval,
        matchedRulesCount: matchedRules.length
      });

      return { requiresApproval, matchedRules };
    }, { operation: 'evaluateApprovalRequirement', table: 'approval_rules' });
  }
}
